import { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from '../../../../utils/format/formatDistanceToNow';
import { formatText } from '../../../../utils/format/formatText';
import { useIsMobile } from '../../../../utils/getIsMobile';
import { openExternalLink } from '../../../../utils/openExternalLink';
import { makeTransition } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { BusinessType } from '../../../business/types';
import { IconKebab } from '../../../icon/IconKebab';
import { IconWebsite } from '../../../icon/IconWebsite';
import { IconButton } from '../../../input/IconButton';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex } from '../../../layout/Flex';
import { Typography } from '../../../typography/Typography';
import { UserUI } from '../../../users/types';
import { PostAILabel } from '../../PostAILabel/PostAILabel';
import { PostUI, ProductItemUI } from '../../types';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { TextContainer, TextContent, TextFooter } from '../styles';
import { PostProducts } from './PostProducts/PostProducts';
import { PostTextCatalogLink } from './PostTextCatalogLink/PostTextCatalogLink';
import { PostTextControls } from './PostTextControls';
import { PostTextProfile } from './PostTextProfile';
import { PostTextSiteLink } from './PostTextSiteLink/PostTextSiteLink';

interface PostTextProps {
    post: PostUI;
    index: number;
    user?: UserUI;
    onClickOutClick: PostDetailsCardProps['onClickOutClick'];
    onProductButtonClick: PostDetailsCardProps['onProductButtonClick'];
    onPostTextProductsClick: (product: ProductItemUI) => void;
    onPostTextCatalogClick: VoidFunction;
    onPostTextClickoutClick: VoidFunction;
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
    onSubscribe?: PostDetailsCardProps['onSubscribe'];
    renderMenu: (trigger: ReactElement) => ReactElement | null;
}

const KebabTrigger = (
    <Box
        transition={makeTransition({
            styles: ['opacity'],
            ms: 200,
        })}
    >
        <IconButton size="s" cursor={'pointer'} padding={0}>
            <Flex
                width={32}
                height={32}
                borderRadius="50%"
                backgroundColor="var(--control-secondary-background)"
                justifyContent="center"
                alignItems="center"
            >
                <IconKebab
                    fontSize={16}
                    color="var(--color-primary)"
                    dataTestId={DataTestId.POST_CARD_KEBAB}
                />
            </Flex>
        </IconButton>
    </Box>
);

const CUT_BREAKPOINT_HEIGHT = 44;
const CUT_TEXT_CONTAINER_MAX_HEIGHT = 54;

export const PostText: FC<PostTextProps> = ({
    post,
    index,
    user,
    onPostTextProductsClick,
    onPostTextCatalogClick,
    onPostTextClickoutClick,
    onReaction,
    onBookmark,
    onShare,
    onSubscribe,
    renderMenu,
}) => {
    const { createdAt, likes, text, business, products } = post;
    const { sizes } = usePostDetailsCardContext();
    const t = useTranslation();
    const isMobile = useIsMobile();
    const [isCut, setIsCut] = useState(true);
    const textRef = useRef<HTMLDivElement>(null);

    const onCutClick = useCallback(() => {
        setIsCut(false);
    }, []);

    const handleWebsiteClick = useCallback(() => {
        if (business?.siteUrl) {
            openExternalLink(business.siteUrl);
        }
    }, [business?.siteUrl]);

    useEffect(() => {
        if (textRef.current && textRef.current?.offsetHeight <= CUT_BREAKPOINT_HEIGHT) {
            setIsCut(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textRef.current]);

    if (sizes.height === 0) {
        return null;
    }

    if (isMobile) {
        return (
            <>
                <TextContainer
                    isMobile
                    maxHeight={isCut ? CUT_TEXT_CONTAINER_MAX_HEIGHT : undefined}
                    height="auto"
                >
                    <Flex direction={'column'} height={'100%'}>
                        <TextContent isMobile dataTestId={DataTestId.POST_TEXT} isCut={isCut}>
                            <span ref={textRef}>{formatText(text)}</span>
                        </TextContent>
                    </Flex>
                    {isCut ? (
                        <Typography
                            onClick={onCutClick}
                            variant="body2"
                            weight="medium"
                            mt={3.25}
                            mr={2}
                        >
                            {t('ещё')}
                        </Typography>
                    ) : undefined}
                </TextContainer>
                <TextContainer isMobile>
                    <Flex direction="column" width="100%" px={2}>
                        <TextFooter dataTestId={DataTestId.POST_TEXT_FOOTERT}>
                            <Box mr={1.5}>{formatDistanceToNow(createdAt)}</Box>
                            {likes > 10 && (
                                <Box display="inline" as="span" mr={1.5}>
                                    {t('Понравилось: ')}
                                    {likes}
                                </Box>
                            )}
                            {post.isAIContent ? (
                                <PostAILabel
                                    spacing={0.5}
                                    iconHeight={12}
                                    iconWidth={12}
                                    text={t('Cоздано с помощью ИИ')}
                                />
                            ) : null}
                        </TextFooter>
                    </Flex>
                </TextContainer>
            </>
        );
    }

    return (
        <TextContainer isMobile={false} height={sizes.height}>
            <Flex direction={'column'} height={'100%'}>
                <>
                    <Flex
                        width="100%"
                        px={2}
                        py={1}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <PostTextProfile
                            user={user}
                            post={post}
                            index={index}
                            onSubscribe={onSubscribe}
                            maxWidth="calc(100% - 28px)"
                        />
                        <Flex direction="row" alignItems="center" spacing={0.5}>
                            {business?.type === BusinessType.BUSINESS &&
                                Boolean(business?.siteUrl) && (
                                    <IconButton onClick={handleWebsiteClick} size="s" padding={0}>
                                        <Flex
                                            width={32}
                                            height={32}
                                            borderRadius="50%"
                                            backgroundColor="var(--control-secondary-background)"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            <IconWebsite
                                                fontSize={16}
                                                color="var(--color-primary)"
                                            />
                                        </Flex>
                                    </IconButton>
                                )}
                            <Box alignSelf="center">{renderMenu(KebabTrigger)}</Box>
                        </Flex>
                    </Flex>
                    <PostTextControls
                        post={post}
                        index={index}
                        onReaction={onReaction}
                        onBookmark={onBookmark}
                        onShare={onShare}
                    />
                </>
                <TextContent dataTestId={DataTestId.POST_TEXT} isMobile={false}>
                    <PostProducts
                        products={products}
                        onPostTextProductsClick={onPostTextProductsClick}
                        post={post}
                        onPostTextClickoutClick={onPostTextClickoutClick}
                        onPostTextCatalogClick={onPostTextCatalogClick}
                    />
                    {products.items.length === 0 && (
                        <Flex direction="column" width="100%" spacing={1} mb={3} mt={2}>
                            <PostTextSiteLink
                                post={post}
                                text={t('Перейти на сайт')}
                                onPostTextClickoutClick={onPostTextClickoutClick}
                            />
                            <PostTextCatalogLink
                                post={post}
                                onPostTextCatalogClick={onPostTextCatalogClick}
                            />
                        </Flex>
                    )}
                    {formatText(text)}
                </TextContent>
                <Flex direction="column" width="100%" px={2}>
                    <TextFooter dataTestId={DataTestId.POST_TEXT_FOOTERT}>
                        <Box mr={2}>{formatDistanceToNow(createdAt)}</Box>
                        {likes > 10 && (
                            <Box display="inline" as="span" mr={2}>
                                {t('Понравилось: ')}
                                {likes}
                            </Box>
                        )}
                        {post.isAIContent ? (
                            <PostAILabel
                                spacing={0.5}
                                iconHeight={12}
                                iconWidth={12}
                                text={t('Cоздано с помощью ИИ')}
                            />
                        ) : null}
                    </TextFooter>
                </Flex>
            </Flex>
        </TextContainer>
    );
};
